/**
 * ResizeHandle — the canonical drag-to-resize divider for a side panel.
 *
 * Presentational over `useResizableWidth`: it renders the affordance and owns
 * the interaction, never the width. The consumer holds the state, so layout
 * (drawer paper width, content offset) can react to a drag in the same frame.
 *
 * Interaction (SPEC-FRONTEND-RULE-046):
 * - Pointer: press and drag. The hit area is `hitAreaPx` (24 by default) wide,
 *   meeting the WCAG 2.5.8 target floor without a fat line in the design. The
 *   handle paints NOTHING for hover or for an in-flight drag: the `col-resize`
 *   cursor is the entire pointer affordance, and the panel edge following the
 *   pointer is the entire drag feedback (owner-ruled 2026-08-26; amends
 *   STORY-TA-RESIZABLE-NAV AC-001, which had required a hover/drag highlight).
 * - Keyboard: ArrowLeft/ArrowRight move the divider by 16px, Shift+Arrow by
 *   48px, Home/End jump to the minimum/maximum, Enter or Space resets to the
 *   default width. Double-click also resets.
 * - Screen reader: `role="separator"` with `aria-orientation="vertical"` and a
 *   live `aria-valuenow`/`valuemin`/`valuemax` in CSS pixels, plus an
 *   `aria-valuetext` that reads the width as a translated, plural-correct
 *   sentence rather than a bare number (RULE-047).
 *
 * The ONE surviving emphasis is the keyboard focus indicator, which
 * SPEC-FRONTEND-RULE-046 makes a MUST (WCAG 2.4.7). `:focus-visible` never
 * matches a pointer interaction, so a mouse user sees no highlight at any
 * point — it lights up only for a keyboard user, who has no cursor to read.
 *
 * Visual values come from `theme.palette` / the `colors` tokens — no inline hex
 * (SPEC-FRONTEND-RULE-034).
 *
 * @see TASK-TARN-RESIZE-HANDLE
 * @see STORY-TA-RESIZABLE-NAV AC-001 / AC-006
 */
import React, { useCallback } from "react";
import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";
import { msg, plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import {
  RESIZE_NUDGE_STEP_LARGE_PX,
  RESIZE_NUDGE_STEP_PX,
  type UseResizableWidthResult,
} from "./useResizableWidth";

export interface ResizeHandleProps {
  /**
   * The state object returned by `useResizableWidth`. The handle reads
   * `width`/`min`/`max`/`isDragging` from it and calls `startDrag`/`nudge`/
   * `reset` on interaction.
   */
  resize: UseResizableWidthResult;
  /**
   * Accessible name for the separator — say WHICH panel it resizes, e.g.
   * "Resize the workspace navigation". Required: a page with two resizable
   * panels needs two distinguishable names.
   */
  ariaLabel: string;
  /**
   * Width of the invisible pointer/touch hit area in CSS pixels. Must stay
   * >= 24 to satisfy the WCAG 2.5.8 target floor (RULE-046).
   *
   * @default 24
   */
  hitAreaPx?: number;
  /**
   * Positioning mode.
   * - `"absolute"` (default): the handle is absolutely positioned on the panel's
   *   trailing edge. The panel must be a positioned ancestor.
   * - `"static"`: the handle participates in flex flow — use it when the panel
   *   and the content area are flex siblings and a real column is acceptable.
   *
   * @default "absolute"
   */
  position?: "absolute" | "static";
  /** Which edge of the panel the handle sits on. @default "right" */
  side?: "left" | "right";
  /** Optional test id, forwarded to the separator element. */
  "data-testid"?: string;
}

/**
 * Drag-to-resize divider for a side panel.
 *
 * Renders nothing when `resize.enabled` is false, so a consumer can keep the
 * call unconditional across breakpoints (hooks stay at the top level) while the
 * affordance disappears in responsive states that own their own width.
 *
 * @example
 * <Box sx={{ position: "relative", width: resize.width }}>
 *   <Nav />
 *   <ResizeHandle resize={resize} ariaLabel={t`Resize the navigation`} />
 * </Box>
 */
export function ResizeHandle({
  resize,
  ariaLabel,
  hitAreaPx = 24,
  position = "absolute",
  side = "right",
  "data-testid": dataTestId,
}: ResizeHandleProps): React.ReactElement | null {
  const theme = useTheme();
  const { _: lingui } = useLingui();
  const { width, min, max, enabled, startDrag, nudge, reset } = resize;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? RESIZE_NUDGE_STEP_LARGE_PX : RESIZE_NUDGE_STEP_PX;
      // A left-edge handle grows the panel when the divider moves left.
      const direction = side === "right" ? 1 : -1;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          nudge(-step * direction);
          break;
        case "ArrowRight":
          event.preventDefault();
          nudge(step * direction);
          break;
        case "Home":
          event.preventDefault();
          nudge(min - width);
          break;
        case "End":
          event.preventDefault();
          nudge(max - width);
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          reset();
          break;
        default:
          break;
      }
    },
    [max, min, nudge, reset, side, width],
  );

  if (!enabled) return null;

  const activeColor = theme.palette.primary.main;

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuetext={plural(width, { one: "# pixel wide", other: "# pixels wide" })}
      title={lingui(msg`Drag to resize. Double-click to reset.`)}
      tabIndex={0}
      data-testid={dataTestId}
      onPointerDown={startDrag}
      onDoubleClick={reset}
      onKeyDown={handleKeyDown}
      sx={{
        // The hit area is wide; the painted line is not.
        width: `${hitAreaPx}px`,
        flexShrink: 0,
        alignSelf: "stretch",
        ...(position === "absolute"
          ? {
              position: "absolute",
              top: 0,
              bottom: 0,
              [side]: `-${hitAreaPx / 2}px`,
              zIndex: 1,
            }
          : {}),
        cursor: "col-resize",
        touchAction: "none",
        // A drag must not select the nav labels it passes over.
        userSelect: "none",
        display: "flex",
        justifyContent: "center",
        backgroundColor: "transparent",
        outline: "none",
        // The divider itself: a hairline pseudo-element, so the 24px hit area
        // stays invisible and the line stays 1px. It is painted ONLY for the
        // keyboard focus indicator below — hover and an in-flight drag paint
        // nothing on purpose. Emphasis, when it is painted, is carried by
        // COLOUR and never by thickening the line, because a width change here
        // would nudge the panel content sideways.
        "&::after": {
          content: '""',
          width: "1px",
          alignSelf: "stretch",
          backgroundColor: "transparent",
          transition: theme.transitions.create(["background-color"], {
            duration: theme.transitions.duration.shortest,
          }),
        },
        // Visible focus indicator (SPEC-FRONTEND-RULE-046 — a WCAG 2.4.7 MUST,
        // and the only emphasis this handle paints). Deliberately NOT paired
        // with a `:hover` rule: `:focus-visible` does not match a pointer
        // interaction, so this is invisible to a mouse user by construction.
        "&:focus-visible": {
          backgroundColor: alpha(activeColor, 0.12),
        },
        "&:focus-visible::after": {
          backgroundColor: activeColor,
        },
      }}
    />
  );
}

export default ResizeHandle;
